// SPDX-License-Identifier: BUSL-1.1

/**
 * Read the projected status surfaces for one agent from the new endpoint
 * landed in ADR 008 Phase 2: `GET /api/agents/:agentId/surfaces`.
 *
 * This hook drives Phase 3A drilldowns (tooltips, "why am I seeing this?"
 * detail sheets). It's gated behind the feature flag
 * `status-surface-drilldown` so the data path stays cold until the Phase 2
 * dual-run window proves zero divergence.
 *
 * Graceful degradation: when the flag is off, the query is disabled — no
 * network calls. When the flag is on but the endpoint errors (e.g., the
 * substrate hasn't shipped to this environment yet), the hook returns
 * `data=undefined` and `isError=true` rather than throwing. Components
 * that consume this hook MUST render correctly when `data` is missing —
 * the existing surface UI (AttestationIndicator, AgentBudgetCard) is the
 * fallback per ADR 008's "ZERO visible UI changes when flag is off" rule.
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import type { AgentSurfaceResponse } from '@/types/api';

/** Feature flag key gating Phase 3A drilldowns. URL override: `?ff_status-surface-drilldown=true`. */
export const STATUS_SURFACE_DRILLDOWN_FLAG = 'status-surface-drilldown';

interface UseAgentSurfacesOptions {
  /** Pass false to force-disable the query regardless of the feature flag. */
  enabled?: boolean;
}

interface UseAgentSurfacesResult {
  data: AgentSurfaceResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  /** True when the feature flag is off — no fetch happened. */
  flagDisabled: boolean;
}

export function useAgentSurfaces(
  agentId: string | undefined,
  options: UseAgentSurfacesOptions = {},
): UseAgentSurfacesResult {
  const flagEnabled = useFeatureFlag(STATUS_SURFACE_DRILLDOWN_FLAG);
  const enabled = (options.enabled ?? true) && flagEnabled && Boolean(agentId);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['agent-surfaces', agentId],
    queryFn: () => apiFetch<AgentSurfaceResponse>(`/agents/${encodeURIComponent(agentId!)}/surfaces`),
    enabled,
    staleTime: 30_000,
    retry: 1,
  });

  return {
    data,
    isLoading,
    isError,
    flagDisabled: !flagEnabled,
  };
}
